import { useState, useEffect } from "react";
import { Tooltip, Spin } from "antd";
import { Flex } from "@chakra-ui/layout";
import { MdAssignment, MdCheckCircle, MdClose } from "react-icons/md";
import { useFilters } from "core/features/filters/store";
import OperacoesTable from "./OperacoesTable";
import useStore from "../../../OrdemServico/hooks/useStore";
import useModalOrdemServicoMatriz from "../../../OrdemServico/hooks/useModalOrdemServicoMatriz";
import {
  StyledModal,
  Title,
  StyledTag,
  ButtonStyled,
  ButtonStyledLink,
  ContentButton,
  StyledCheckbox,
  SelectCompact,
  SpinContainer,
} from "./styles";

interface ModalMatrizOperacoesProps {
  data: any;
  isLoading: boolean;
  talhoesIds: any[];
  planoOperacionalData: any;
  isOpen: boolean;
  onClose: () => void;
  next: () => void;
  setOperationName: (args: string) => void;
  filteredResult: any[];
  orderedResult: any[];
  isEdit?: boolean;
  ordemServico: {
    id: string | null;
    code: number | null;
  };
  listData: any[];
  isFetching: boolean
}

function ModalMatrizOperacoes({
  data,
  isLoading,
  talhoesIds,
  planoOperacionalData,
  isOpen,
  onClose,
  next,
  setOperationName,
  filteredResult,
  orderedResult,
  isEdit,
  ordemServico,
  listData,
  isFetching
}: ModalMatrizOperacoesProps) {

  const [viewCompact, setViewCompact] = useState(false);
  const [available, setAvailable] = useState(0);

  const {
    classesOperacoes,
    planoOperacionalOperacaoTalhaoIds,
    setClassesOperacoes,
    setPlanoOperacionalOperacaoTalhaoIds,
  } = useStore();

  const classes = Array.from(new Set(classesOperacoes));

  const {
    setHiddenBlockeds,
    setHiddenCompleteds,
  } = useModalOrdemServicoMatriz();

  const { filters } = useFilters();
  const { filtersAdvanced } = filters;

  const operacoesSelectedIds = filtersAdvanced.operacao;
  const operacoesFilters = planoOperacionalData.filtrosAvancados.operacoes;
  const operacoesSelected = operacoesFilters?.filter((o: any) => operacoesSelectedIds?.includes(o.operacaoId));
  const areaTotal = listData?.reduce((acc: number, item: any) => acc += Number(item.areaPlantadaHa), 0);

  const operacoes = data?.flatMap((operacao: any) => operacao?.planoOperacionalOperacaoTalhao?.map((operacaoTalhao: any) => operacaoTalhao.operacaoClasse));
  const operacoesSet = Array.from(new Set(operacoes));

  function getOperacoesDisponiveis() {
    const operacoes = filteredResult?.flatMap((t: any) => t.planoOperacionalOperacaoTalhao);
    return operacoes?.filter(
      (o: any) =>
        !o.bloqueado &&
        !o.isPlaceholder &&
        ((!isEdit && o.areaDisponivelPorcentagem > 0) || isEdit) &&
        o.planoOperacionalOperacaoTalhaoId
    ) ?? [];
  }

  function handleSelectAllAvailable(checked: boolean) {
    const operacoesFiltered = getOperacoesDisponiveis();
    const ids = operacoesFiltered?.map((o: any) => o.planoOperacionalOperacaoTalhaoId);
    const classesTalhoes = Array.from(new Set(operacoesFiltered.map((t: any) => t?.operacaoClasse)));
    setAvailable(ids.length);
    if (checked) {
      setClassesOperacoes(classesTalhoes as string[]);
      setPlanoOperacionalOperacaoTalhaoIds(ids);
      return;
    }
    setClassesOperacoes([]);
    setPlanoOperacionalOperacaoTalhaoIds([]);
  }

  function handleHiddenCompact() {
    setViewCompact(!viewCompact);
    setOperationName("");
  }

  useEffect(() => {
    if (isOpen && filteredResult) {
      const operacoesFiltered = getOperacoesDisponiveis();
      setAvailable(operacoesFiltered.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filteredResult]);

  return (
    <StyledModal
      centered
      width={"95%"}
      zIndex={999999}
      visible={isOpen}
      onCancel={onClose}
      destroyOnClose
      title={
        <Title>
          <MdAssignment color="#fff" size={28} />
          {isEdit ? "Edição " : "Criação"} de Ordem de Serviço - Matriz de Operações {isEdit && ` · O.S. ${ordemServico?.code}`}
        </Title>
      }
      footer={
        <Flex justifyContent="space-between" alignItems="center">
          <p><b>{planoOperacionalOperacaoTalhaoIds.length ?? 0}</b> operações selecionadas</p>
          <ContentButton>
            <ButtonStyledLink
              size="large"
              onClick={onClose}
              loading={isLoading}
              icon={<MdClose size={24} />}
            >Cancelar</ButtonStyledLink>
            <ButtonStyled
              type="primary"
              size="large"
              onClick={next}
              loading={isLoading}
              icon={<MdCheckCircle size={24} />}
            >Próximo</ButtonStyled>
          </ContentButton>
        </Flex>
      }
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        borderBottom="1px solid #cecece"
        padding={4}
      >
        <Flex gridGap={8} alignItems="center">
          <div>
            <p style={{ color: "#666666", fontWeight: 600 }}>Talhões Selecionados</p>
            <Flex gridGap={1} marginTop={1}>
              {listData?.slice(0, 4)?.map((item: any) => (
                <StyledTag key={item.codigo} color="blue">{item.codigo}</StyledTag>
              ))}
              {listData?.length > 4 && (
                <Tooltip title={listData.slice(4).map((item: any) => item.codigo).join(", ")}>
                  <StyledTag color="blue">+{listData.length - 4}</StyledTag>
                </Tooltip>
              )}
            </Flex>
          </div>
          {classes?.length > 0 && (
            <div>
              <p style={{ color: "#666666", fontWeight: 600 }}>Classes de Operações Selecionadas</p>
              <Flex gridGap={1} marginTop={1}>
                {classes?.slice(0, 4)?.map((item: any) => (
                  <StyledTag color="green">{item}</StyledTag>
                ))}
                {classes?.length > 4 && (
                  <Tooltip title={classes?.slice(4)?.map((item: any) => item)?.join(", ")}>
                    <StyledTag color="green">+{classes?.slice(4)?.length}</StyledTag>
                  </Tooltip>
                )}
              </Flex>
            </div>
          )}
          {operacoesSelected?.length > 0 && (
            <div>
              <p style={{ color: "#666666", fontWeight: 600 }}>Operações Selecionadas</p>
              <Flex gridGap={1} marginTop={1}>
                {operacoesSelected?.map((item: any) => (
                  <StyledTag color="green">{item.descricao}</StyledTag>
                ))}
              </Flex>
            </div>
          )}
        </Flex>

        <Flex gridGap={8} alignItems="center" textAlign="center">
          <div>
            <b style={{ color: "#2196f3" }}>{talhoesIds.length ?? 0}</b>
            <p>TALHÕES</p>
          </div>
          <div>
            <b style={{ color: "#2196f3" }}>{areaTotal ? areaTotal.toFixed(2) : 0}</b>
            <p>ÁREA (HA)</p>
          </div>
          <div>
            <b style={{ color: "#2196f3" }}>{planoOperacionalOperacaoTalhaoIds.length ?? 0}</b>
            <p>SELECIONADAS</p>
          </div>
        </Flex>
      </Flex>

      <Flex justifyContent="flex-end" borderBottom="1px solid #cecece" padding={4}>
        <StyledCheckbox
          checked={planoOperacionalOperacaoTalhaoIds.length > 0 && available === planoOperacionalOperacaoTalhaoIds.length}
          onChange={e => handleSelectAllAvailable(e.target.checked)}
          disabled={orderedResult.length === 0}
        >Marcar Todas Disponíveis</StyledCheckbox>
        <StyledCheckbox onChange={(e) => setHiddenCompleteds(e.target.checked)} disabled={orderedResult.length === 0}>Ocultar Concluídas</StyledCheckbox>
        <StyledCheckbox onChange={(e) => setHiddenBlockeds(e.target.checked)} disabled={orderedResult.length === 0}>Ocultar Bloqueadas</StyledCheckbox>
        <StyledCheckbox onChange={handleHiddenCompact} disabled={orderedResult.length === 0}>Visão por Classe</StyledCheckbox>
        {viewCompact && (
          <SelectCompact>
            <select onChange={(e) => setOperationName(e.target.value)}>
              <option value="">Selecione a classe</option>
              {operacoesSet.map((item: any, index: number) => (
                <option key={index} value={item}>{item}</option>
              ))}
            </select>
          </SelectCompact>
        )}
      </Flex>

      {isLoading ? (
        <SpinContainer>
          <Spin />
        </SpinContainer>
      ) : (
        <OperacoesTable
          isEdit={isEdit}
          listData={listData}
          isFetching={isFetching}
          ordemServico={ordemServico}
          operacoes={orderedResult ?? []}
        />
      )}
    </StyledModal>
  );
}

export default ModalMatrizOperacoes;
